import { useState } from "react";
import { Navigate, Outlet, useMatch } from "react-router";
import { isTeacherSide, teachesEnglish } from "@edu/shared";
import { useFlashcardsToday } from "../flashcards/use-flashcards-today.ts";
import { useSession } from "../../lib/session.tsx";
import { TimeZoneGate } from "./TimeZoneGate.tsx";
import { HomeworkNewsBar, HomeworkNewsRefresh, useHomeworkNews } from "./HomeworkNews.tsx";
import { ConversationNewsProvider, useConversationNews } from "./ConversationNews.tsx";
import { useChangelogNews } from "./ChangelogNews.tsx";
import { BuildFreshnessBar } from "./BuildFreshnessBar.tsx";
import { TtsProgressGate } from "./TtsProgressGate.tsx";
import { SideNav } from "./SideNav.tsx";
import { TabBar } from "./TabBar.tsx";
import { Dialog } from "./Dialog.tsx";
import { ChangePasswordDialog } from "../auth/ChangePasswordDialog.tsx";
import { TrackingDialog } from "../students/TrackingDialog.tsx";
import { SubjectsDialog } from "./SubjectsDialog.tsx";
import { BoardNameDialog } from "./BoardNameDialog.tsx";
import { ThemeDialog } from "./ThemeDialog.tsx";
import { ColleagueReferralDialog } from "../referral/ColleagueReferralDialog.tsx";
import { useFeedback } from "../feedback/feedback-context.tsx";
import { Loading } from "../../lib/loading.tsx";

/**
 * Каркас кабинета: сайдбар слева, экран справа.
 *
 * Вариант 1a из docs/design/cabinet-ux-v1.html: разделы всегда на виду,
 * действия самой сущности живут в шапке рабочей области, а не в меню.
 *
 * Четыре правила, каждое из которых не украшение:
 *
 * 1. **В комнате урока и в архиве сайдбара нет вовсе.** Там своё шасси:
 *    доска во весь экран, острова Excalidraw по всем четырём краям. Поэтому
 *    маршруты `/lesson/*`, `/archive/*` и `/join/*` стоят ВНЕ этого макета,
 *    а не прячут его условием внутри
 * 2. **В редакторе заготовки сайдбар сам сворачивается в полосу значков.**
 *    Слайд это жёсткие 16:9, и двести двадцать пикселей у него отнимать
 *    нельзя. Автоматически, а не кнопкой: человек, зашедший поправить слайд,
 *    не должен сначала прибирать интерфейс
 * 3. **Раскладку навигации решает CSS, а не React.** Выше порога узкой
 *    раскладки это сайдбар, развёрнутый или полосой значков; ниже порога (и на
 *    грубом указателе до 76rem) сайдбара нет ни у кого, а навигация уезжает вниз
 *    полосой из четырёх пунктов. Обе поверхности стоят в разметке ВСЕГДА, и
 *    видимость выбирает `display`: ветка по ширине в React означала бы второй
 *    источник правды о раскладке, который расходится с медиазапросом на первом
 *    же повороте телефона. Решение Р2, `docs/phone-design-decisions.md`
 * 4. **Состояние у навигации ровно одно: лист «Ещё»**, и он ОКНО, а не
 *    выдвижной ящик. Общий примитив `Dialog.tsx`, то есть Esc, ловушка фокуса и
 *    `inert` для фона достаются от браузера, а не пишутся здесь. Рисуется только
 *    открытым, иначе в разметке жили бы две копии навигации разом
 *
 * Сами разделы и подвал живут в `SideNav.tsx`: мест, где их надо нарисовать,
 * стало два (сайдбар и лист), и вторая копия разъехалась бы с первой молча.
 */
export function Shell() {
  const { state, signOut, adopt } = useSession();
  /**
   * Смена пароля окном, а не адресом.
   *
   * ⚠️ Это состояние и четыре соседних НЕ адреса, в отличие от карточки
   * ученика. Разница в том, кому нужна ссылка: карточку ученика пересылают и
   * открывают в соседней вкладке, а «сменить пароль» никто никогда не пришлёт
   * ссылкой. Свой адрес стоил бы записи в истории, из которой «назад»
   * возвращает в наполовину заполненную форму пароля.
   */
  const [changingPassword, setChangingPassword] = useState(false);
  const [tracking, setTracking] = useState(false);
  /** Мои предметы: окном по той же причине, что учёт занятий. */
  const [subjects, setSubjects] = useState(false);
  /** Подпись над курсором на доске: окном по той же причине, что смена пароля. */
  const [boardName, setBoardName] = useState(false);
  /**
   * Светлая или тёмная. Окном по той же причине, что подпись на доске, и стоит
   * рядом с ней же: это личная настройка браузера, а не работа с учениками.
   */
  const [theming, setTheming] = useState(false);
  /**
   * Позвать коллегу. Окном по той же причине, что «Мои предметы». ⚠️ Здесь
   * живёт только «открыто ли», а не введённое: черновик формы держит само окно
   * и теряет его при закрытии (6.2 `docs/colleague-referral-design.md`).
   */
  const [referring, setReferring] = useState(false);
  /**
   * Лист «Ещё»: полный сайдбар на узкой раскладке. Своего адреса нет по той же
   * причине, что у окон выше: «назад» из него возвращало бы на тот же экран с
   * закрытым листом, то есть выглядело бы как несработавшая кнопка.
   */
  const [more, setMore] = useState(false);
  /** Выход идёт: досылка очередей перед ним бывает небыстрой (`SideNav`, `leaving`). */
  const [leaving, setLeaving] = useState(false);
  /**
   * Редактор заготовки узнаём по маршруту, а не по флагу от страницы: флаг
   * пришлось бы прокидывать через `Outlet`, и он расходился бы с адресом.
   */
  const editing = useMatch("/plans/:planId") !== null;
  /**
   * Расписание. Признак работает на два дела сразу, и оба ниже в разметке:
   * полоса про заданное стоит только над этим экраном, а сам экран с 17.09.2026
   * владеет высотой (`data-fixed`), то есть страницей не прокручивается.
   */
  const onSchedule = useMatch("/schedule") !== null;
  /**
   * Заметка о неточности. Само окно рисует провайдер, стоящий выше маршрутов,
   * отсюда только вызов `open()`.
   *
   * ⚠️ Хук стоит здесь, ДО обеих ранних отдач ниже (заглушка загрузки и
   * переадресация без сессии). Ниже них он оказался бы вызванным не на каждом
   * рендере, а это ровно то, что React запрещает: состав хуков обязан
   * совпадать от рендера к рендеру.
   */
  const feedback = useFeedback();
  /**
   * Сколько работ ученику задали и он их не открывал: число у раздела «Домашка»
   * и полоса на расписании (`HomeworkNews.tsx`).
   *
   * ⚠️ Хук стоит здесь по той же причине, что `useFeedback`: ДО обеих ранних
   * отдач ниже. Условие «есть ли преподаватели» уезжает в него параметром, а не
   * ветвлением вокруг вызова, иначе состав хуков менялся бы от рендера к
   * рендеру ровно в момент, когда приезжает ответ `getMe`.
   */
  const news = useHomeworkNews(state.status === "signed" && state.me.learning === true);
  /**
   * Сколько новых сообщений у человека: число у раздела «Сообщения» и повод
   * перечитать открытое окно переписки (`ConversationNews.tsx`).
   *
   * ⚠️ Хук стоит здесь по той же причине, что три соседних: ДО обеих ранних
   * отдач ниже. Условие уезжает в него параметром, а не ветвлением вокруг
   * вызова, иначе состав хуков менялся бы от рендера к рендеру ровно в момент,
   * когда приезжает ответ `getMe`.
   *
   * ⚠️ Условие это «есть сессия», без разбора роли: разговоры бывают у обеих
   * сторон, и опрос, включённый только преподавателю, оставил бы ученика без
   * единственной подсказки о новом сообщении.
   */
  const messages = useConversationNews(state.status === "signed");
  /**
   * Сколько записей «Что нового» появилось с тех пор, как человек читал список
   * (`ChangelogNews.tsx`). Ни одного запроса: записи в бандле, указатель в
   * сессии.
   *
   * ⚠️ Хук стоит здесь по той же причине, что три соседних: ДО обеих ранних
   * отдач ниже. Оба условия уезжают в него параметрами, а не ветвлением вокруг
   * вызова.
   *
   * ⚠️ Сторона считается ЗДЕСЬ через `isTeacherSide`, но сам вызов `isTeacherSide`
   * стоит ниже ранних отдач, поэтому условие написано так же, как правило в нём:
   * ученику (`learning && !teaching`) список не показывается и чанк записей на
   * его канал не уезжает вовсе. Список написан голосом преподавателя («вы
   * отмечаете, кто был на занятии»), и ученику он не адресован ни строкой.
   */
  /**
   * Карточки ученика: сколько показать сегодня (10.2 `docs/flashcards-design.md`).
   * Хук стоит здесь по той же причине, что соседние: ДО ранних отдач ниже.
   * Опрос только у того, у кого есть преподаватель, и не в просмотре кабинета:
   * тренажёр там предпросмотр, а счёт чужой.
   */
  const flashcards = useFlashcardsToday(
    state.status === "signed" && state.me.learning === true && !state.me.viewer,
  );
  const whatsNew = useChangelogNews(
    state.status === "signed" && isTeacherSide(state.me),
    state.status === "signed" ? state.me.changelogSeenAt : undefined,
  );

  /**
   * Пока сессия неизвестна, экрана нет вовсе.
   *
   * ⚠️ Именно заглушка, а не `<Outlet />`: раздел, отрисованный раньше ответа
   * `getMe`, успевает сходить за своим списком и показать красное «HTTP 401»
   * за миг до того, как станет ясно, что человек просто не вошёл. Раньше это
   * держал корневой экран, который был единственной дверью в кабинет.
   */
  if (state.status === "loading") {
    return (
      <main className="lobby">
        <Loading />
      </main>
    );
  }

  /**
   * ⚠️ Без сессии уводим на корень, где стоит форма входа. Это не удобство, а
   * восстановление поведения, которое до разделения кабинета держалось само
   * собой: раньше кабинет жил на корне, и «выйти» просто подменяло его формой
   * входа. Теперь разделов четыре, и без этой строки выход из любого из них
   * оставлял бы человека на экране, который отвечает «HTTP 401: no_session».
   * Тем же путём чинится и прямой заход по адресу раздела без сессии.
   */
  if (state.status !== "signed") return <Navigate to="/" replace />;

  /**
   * Разделы преподавателя.
   *
   * ⚠️ Правило считает `isTeacherSide`, и живёт оно в общем пакете затем, чтобы
   * не написаться второй раз иначе. Смысл его такой: прячем разделы только у
   * того, кто ТОЛЬКО ученик. Человек без пар вовсе это преподаватель, который
   * ещё никого не завёл, и спрятать от него «Учеников» значило бы спрятать
   * единственную дверь к работе; ученик же попадает в кабинет по ссылке
   * приглашения, то есть его пара существует раньше первого экрана.
   *
   * Что прячем и почему именно это: «Ученики» и «Заготовки» у ученика пусты
   * всегда, а их ручки отвечают ему 404; «Материалы» это личное хранилище
   * преподавателя, и квота под ними тоже его. Остаются «Расписание», «Уроки» и
   * «Архив», то есть ровно то, что у ученика своё.
   */
  const teacher = isTeacherSide(state.me);
  /**
   * Пункт «Позвать коллегу» (6.1 `docs/colleague-referral-design.md`). Три
   * условия: сторона преподавателя (сессии, выданной ссылкой ученика, сервер
   * отдаёт `teaching: false`, и `teacher` у неё ложь), привязанный вход
   * (`claimed`: иначе гость без пар считался бы преподавателем) и не просмотр
   * кабинета админом, где ручка ответила бы `view_only`. Это удобство, а не
   * правило: правило держит сервер.
   */
  const referral = teacher && state.me.claimed && !state.me.viewer;

  /**
   * Разделы и подвал: один и тот же набор в двух поверхностях. Собран функцией,
   * а не переменной с готовым узлом, ровно из-за `onPick`: в сайдбаре нажатие
   * пункта ничего не закрывает, в листе оно закрывает лист.
   */
  const nav = (onPick?: () => void) => (
    <SideNav
      me={state.me}
      teacher={teacher}
      homework={news.count}
      messages={messages.total}
      whatsNew={whatsNew}
      flashcards={state.me.learning === true ? flashcards.total : null}
      decks={teacher && teachesEnglish(state.me.subjects ?? null)}
      feedback={feedback.enabled}
      referral={referral}
      onTracking={() => setTracking(true)}
      onSubjects={() => setSubjects(true)}
      onBoardName={() => setBoardName(true)}
      onTheme={() => setTheming(true)}
      onPassword={() => setChangingPassword(true)}
      // Стрелка, а не `feedback.open` ссылкой: так в `open()` не уезжает
      // объект события, которого он не ждёт, и не теряется `this`
      onFeedback={() => feedback.open()}
      onReferral={() => setReferring(true)}
      onSignOut={() => {
        setLeaving(true);
        void signOut().finally(() => setLeaving(false));
      }}
      leaving={leaving}
      onPick={onPick}
    />
  );

  return (
    /*
      ⚠️ Признаков два, и совпадают они не всегда. `data-compact` это сайдбар
      полосой значков, и нужен он только редактору заготовки: слайд это жёсткие
      16:9, и двести двадцать пикселей у него отнимать нельзя. `data-fixed` это
      «экран владеет высотой, страница не прокручивается», и таких экранов с
      17.09.2026 два. Расписанию оно нужно не ради места, а ради верхней кромки:
      на iPhone содержимое, уезжающее мимо неё, видно в зазоре под строкой
      состояния, накрыть его изнутри страницы нечем, и единственный ответ это не
      возить мимо кромки ничего (разбор в `docs/schedule-calendar-design.md`,
      2.5). Календарь взамен прокручивается внутри себя.
    */
    <div className="shell" data-compact={editing} data-fixed={editing || onSchedule}>
      <nav className="side" aria-label="Разделы">
        <div className="side__me">
          <span className="side__avatar" aria-hidden="true">
            {firstLetter(state.me.displayName)}
          </span>
          <span className="side__label side__name">{state.me.displayName}</span>
        </div>
        {nav()}
      </nav>

      <div className="shell__main">
        {/*
          Требование указать пояс стоит НАД рабочей областью, а не внутри
          экранов: пояс нужен всем разделам сразу, и повторять его в каждом
          означало бы забыть в одном. Исчезает навсегда после подтверждения, а
          дверь к смене пояса живёт после этого на расписании, рядом с
          временами, которые от него зависят (`TimeZoneGate`).
        */}
        <TimeZoneGate me={state.me} onDone={adopt} />
        {/*
          Полоса «вышла новая версия». Стоит ЗДЕСЬ, внутри каркаса кабинета, и
          это же единственный запрет на её появление в комнате урока: комната,
          архив и погашение ссылки живут вне каркаса. Разбор в шапке компонента.
        */}
        <BuildFreshnessBar />
        {/*
          Баннер озвучки, заказанной из заготовки. Стоит здесь по той же
          причине, что полоса версии: в комнате урока и в архиве его нет МЕСТОМ.
          Сам баннер ленивый, здесь только привратник, который не грузит ничего,
          пока намерения нет (`docs/tts-in-plan-design.md`, раздел 5.3).

          В просмотре кабинета (`viewer`) баннера нет вовсе: `userId` там это
          преподаватель, которого смотрят, сессия только на чтение, а
          намерения в этом браузере принадлежат смотрящему, а не владельцу
          кабинета.
        */}
        <TtsProgressGate userId={state.me.viewer ? null : state.me.userId} />
        {/*
          Полоса про заданное стоит ТОЛЬКО на расписании, в отличие от полосы
          пояса над ней. Расписание это домашняя страница и то место, куда
          приземляется ученик, погасивший ссылку приглашения, а над самим
          разделом «Домашка» полоса стояла бы поверх списка, о котором говорит.
          Разбор целиком в шапке `HomeworkNews.tsx`.

          Маршрут читается здесь, а не флагом от страницы, по той же причине,
          что и у редактора заготовки строкой выше: флаг пришлось бы протащить
          через `Outlet`, и он разошёлся бы с адресом.
        */}
        {onSchedule && <HomeworkNewsBar count={news.count} />}
        {/*
          Просьба перечесть счёт доезжает до карточки работы: одной смены адреса
          мало, она гонится с запросом самой карточки, который отметку об
          открытии и ставит. Разбор в шапке `HomeworkNews.tsx`.
        */}
        {/*
          Счёт новых сообщений доезжает до обоих экранов переписки: список
          перечитывает себя по изменению счётчика, окно по времени последнего
          сообщения СВОЕГО разговора. Опрос при этом один на вкладку, и живёт он
          здесь, а не в каждом экране: разбор в шапке `ConversationNews.tsx`.
        */}
        <ConversationNewsProvider value={messages}>
          <HomeworkNewsRefresh value={news.refresh}>
            <Outlet />
          </HomeworkNewsRefresh>
        </ConversationNewsProvider>
      </div>

      {/*
        Нижняя полоса стоит ПОСЛЕ рабочей области, а сайдбар до неё: порядок
        обхода читалкой повторяет то, что человек видит глазами. Видна из двух
        поверхностей всегда ровно одна, и решает это `display` (правило 3 выше).
      */}
      <TabBar
        homework={news.count}
        messages={messages.total}
        whatsNew={whatsNew}
        flashcards={flashcards.total}
        onMore={() => setMore(true)}
      />

      {/*
        Окна рисуются последними и вне `.shell__main`: нативный `<dialog>` живёт
        в верхнем слое браузера, и место в разметке на вид не влияет, зато влияет
        на порядок обхода читалкой.
      */}
      {more && (
        <Dialog sheet title="Ещё" onClose={() => setMore(false)}>
          {/*
            Коробку листу даёт `.sheet`, а не `SideNav`: там же включаются
            подписи и квоты, которые узкая раскладка гасит у сайдбара
            переменными `--side-label` и `--side-quota`. Без этого лист показал
            бы столбик безымянных значков, то есть ровно ту рейку, ради отмены
            которой он и заведён.
          */}
          <div className="sheet">{nav(() => setMore(false))}</div>
        </Dialog>
      )}
      {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}
      {tracking && state.status === "signed" && (
        <TrackingDialog me={state.me} onDone={adopt} onClose={() => setTracking(false)} />
      )}
      {subjects && state.status === "signed" && (
        <SubjectsDialog me={state.me} onDone={adopt} onClose={() => setSubjects(false)} />
      )}
      {boardName && state.status === "signed" && (
        <BoardNameDialog me={state.me} onDone={adopt} onClose={() => setBoardName(false)} />
      )}
      {theming && <ThemeDialog onClose={() => setTheming(false)} />}
      {referring && referral && <ColleagueReferralDialog onClose={() => setReferring(false)} />}
    </div>
  );
}

/** Первая буква имени для аватара. Пустое имя даёт точку, а не пустой квадрат. */
function firstLetter(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || "·";
}
